import { useState, useRef, useLayoutEffect } from 'react';
import ProviderBioModal from './ProviderBioModal';

/**
 * Renders a provider bio paragraph clamped to 3 lines in Solo Mode.
 * A "Read more" button appears below the paragraph only when the text
 * is actually truncated (detected via scrollHeight vs clientHeight).
 * Truncation is re-evaluated on every resize via ResizeObserver so the
 * button appears/disappears correctly as the viewport changes width.
 *
 * Clicking "Read more" opens the shared ProviderBioModal.
 *
 * Returns null when providerBio is absent or empty.
 *
 * @param {string} providerBio - The full bio text to render.
 * @param {Object} bookingPage - Full booking page data object (passed to modal).
 */
const ProviderBioReadMore = ({ providerBio, bookingPage }) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const paraRef = useRef(null);

  useLayoutEffect(() => {
    const el = paraRef.current;
    if (!el) return;

    const checkTruncation = () => {
      // +1 tolerance avoids sub-pixel false positives in some browsers.
      // Only update state when the value actually changes to avoid
      // unnecessary re-renders of the always-mounted modal subtree.
      const next = el.scrollHeight > el.clientHeight + 1;
      setIsTruncated((prev) => (prev === next ? prev : next));
    };

    checkTruncation();

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);

    return () => observer.disconnect();
  }, [providerBio]);

  if (!providerBio) {
    return null;
  }

  return (
    <div>
      <p
        ref={paraRef}
        className="text-themed-secondary leading-relaxed line-clamp-3 whitespace-pre-line"
      >
        {providerBio}
      </p>

      {isTruncated && (
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="mt-1 text-sm text-themed-primary underline hover:opacity-80 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:rounded-sm"
        >
          Read more
        </button>
      )}

      <ProviderBioModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        bookingPage={bookingPage}
      />
    </div>
  );
};

export default ProviderBioReadMore;
